import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store";
import { resetTransaction, SendTransactionState, SendTransactionStateRecipient, setError, setLoading, setTransactionAnonymous, setTransactionComment, setTransactionImageUrl, setTransactionMembersOnly, setTransactionBulkQuoteResponse, setTransactionRecipient, setTransactionScanData, setTransactionSendingFees, setTransactionAmountIncludesFees } from "../store/slices/transactionSlice";
import { setTransactionAmount } from "../store/slices/transactionSlice";
import { Transaction, WalletType } from "../models/Transactions";
import { BulkQuoteResponse, PaymentKind, ScanData } from "../repositories/WalletRepository";
import { useEffect, useMemo } from "react";
import { formatAmount, formatAmountWithSATS } from "../utils/Utils";
import { useWallet } from "./useWallet";
import { useWalletTransactions } from "./useWalletTransactions";
import { Pleb, PlebData } from "../models/Pleb";
import { BulkTypes } from "../models/BulkPayment";
import { payFromTransactionState, preparePaymentFromTransactionState, PreparePaymentResult, PreparePaymentResultType, ScanRecipientValidationError, scanRecipientAction } from "../services/transactionService";

export function useTransaction() {
    const dispatch = useDispatch<AppDispatch>();
    const transaction = useSelector((state: RootState) => state.transaction);
    const { balance, walletType, refreshBalance, identityPubkey } = useWallet();
    const { addOrReplaceTransaction } = useWalletTransactions();

    const minAmountSpendable = useMemo(() : number => {
        if(
            transaction.scanData?.kind === PaymentKind.WITHDRAW && 
            transaction.scanData?.minWithdrawableMsat && 
            transaction.scanData?.minWithdrawableMsat > 0
        ) {
            return Math.floor(transaction.scanData?.minWithdrawableMsat / 1000);
        }
        
        return 0;
    }, [transaction.scanData]);

    const maxAmountSpendable = useMemo(() : number => {
        let res = 0;

        const balanceFloat = balance ?? 0;

        if(
            transaction.scanData?.kind === PaymentKind.WITHDRAW && 
            transaction.scanData?.maxWithdrawableMsat && 
            transaction.scanData?.maxWithdrawableMsat > 0
        ) {
            res = transaction.scanData?.maxWithdrawableMsat / 1000;
        }
        else if(transaction.recipient?.bulkPayment) {
            const recipientsCount = transaction.recipient.bulkPayment.recipientsCount;
            res = Math.floor(balanceFloat / recipientsCount) ?? 0;
        }
        else
            res = transaction.scanData?.maxSendable ?? 0;

        if(res > balanceFloat) {
            res = balanceFloat;
        }

        if(res === 0) {
            res = balanceFloat;
        }

        return Math.floor(res);
    }, [transaction.scanData, transaction.recipient, balance]);

    useEffect(() => {
        if(transaction.amount === undefined || transaction.scanData?.fixedAmountMsat !== undefined)
        {
            dispatch(setError(''));
            return;
        }

        if(transaction.scanData?.kind === PaymentKind.WITHDRAW) {
            if(transaction.amount < (minAmountSpendable))
                dispatch(setError('Minimum amount is ' + formatAmountWithSATS(minAmountSpendable)));
            else if(transaction.amount > (maxAmountSpendable))
                dispatch(setError('Maximum amount is ' + formatAmountWithSATS(maxAmountSpendable)));
            else
                dispatch(setError(''));
        } else {
            if(maxAmountSpendable > 0 && transaction.amount > (maxAmountSpendable))
                dispatch(setError('Maximum amount is ' + formatAmountWithSATS(maxAmountSpendable)));
            else
                dispatch(setError(''));
        }
    }, [transaction.amount, transaction.scanData, minAmountSpendable, maxAmountSpendable]);

    const isRecipientValid = useMemo(() => {
        return transaction.recipient !== null || transaction.scanData !== null;
    }, [transaction.recipient, transaction.scanData]);

    const isAmountValid = useMemo(() => {
        return transaction.amount !== undefined && transaction.amount > 0;
    }, [transaction.amount]);

    const transactionState = useMemo(() : SendTransactionState => {
        return {
            ...transaction,
            recipient: transaction.recipient ? {
                ...transaction.recipient,
                pleb: transaction.recipient.pleb ? new Pleb(transaction.recipient.pleb) : null
            } : null
        };
    }, [transaction]);    

    const getSendPaymentFees = (prepareResponse: PreparePaymentResult): number => {
        switch(prepareResponse.type) {
            case PreparePaymentResultType.SEND_PREPARE:
                const paymentMethod = prepareResponse.data.paymentMethod as any;
                switch (paymentMethod.type) {
                    case 'bitcoinAddress': {
                        const feeQuote = paymentMethod.feeQuote?.speedFast;
                        return (feeQuote?.userFeeSat ?? 0) + (feeQuote?.l1BroadcastFeeSat ?? 0);
                    }
                    case 'bolt11Invoice':
                        return (paymentMethod.sparkTransferFeeSats ?? 0) + (paymentMethod.lightningFeeSats ?? 0);
                    case 'sparkAddress':
                    case 'sparkInvoice': {
                        const fee = Number(paymentMethod.fee);
                        return Number.isFinite(fee) ? fee : 0;
                    }
                    default:
                        return 0;
                }
                break;
            case PreparePaymentResultType.LNURL_PREPARE:
                return prepareResponse.data.feeSats;
            default:
                return 0;
        }
    };

    const payTransaction = async () => {
        let ret: Transaction | null = null;

        dispatch(setLoading(true));
        dispatch(setError(''));

        try {
            ret = await payFromTransactionState(walletType, transactionState, transaction.amountIncludesFees, identityPubkey);

            if(ret) {
                dispatch(resetTransaction());
                dispatch(addOrReplaceTransaction(ret));

                if(walletType === WalletType.INTERNAL) {
                    refreshBalance();
                }
            }
        } catch (err: any) {
            dispatch(setError(err.message));
        }

        dispatch(setLoading(false));

        return ret;
    }

    const preparePayment = async () => {
        let ret: boolean = false;

        dispatch(setLoading(true));
        dispatch(setError(''));

        let prepareResponse: PreparePaymentResult | null = null;

        if(walletType === WalletType.SPARK) {            
            try {
                let sendingFees = 0;

                const amountIncludesFees = transactionState.amount == balance;

                prepareResponse = await preparePaymentFromTransactionState(transactionState, amountIncludesFees);
                sendingFees = getSendPaymentFees(prepareResponse);

                if (prepareResponse.type === PreparePaymentResultType.BULK_QUOTE) {
                    const bulkResponse = prepareResponse.data as BulkQuoteResponse;
                    dispatch(setTransactionBulkQuoteResponse(bulkResponse));
                    dispatch(setTransactionRecipient({
                        pleb: null,
                        lnurl: '',
                        bulkPayment: {
                            id: bulkResponse.invoice.bulkData?.bulk.id ?? '',
                            type: bulkResponse.invoice.bulkData?.bulk.type ?? BulkTypes.special,
                            name: bulkResponse.invoice.bulkData?.bulk.name ?? '',
                            imgUrl: bulkResponse.invoice.bulkData?.bulk.imgUrl,
                            recipientsCount: bulkResponse.invoice.bulkData?.bulk.nbToSend ?? 0,
                        },
                    }));
                }

                dispatch(setTransactionSendingFees(sendingFees));
                dispatch(setTransactionAmountIncludesFees(prepareResponse.amountIncludesFees));

                ret = true;
            } catch (err: any) {
                dispatch(setError(err.message));
            }
        } else if(walletType === WalletType.INTERNAL) {
            ret = true;
        }

        dispatch(setLoading(false));
        return ret;
    }
    
    const scanRecipient = async (recipient: SendTransactionStateRecipient) : Promise<{recipient: SendTransactionStateRecipient | null, scanData: ScanData | null}> => {
        let resRecipient: SendTransactionStateRecipient | null = null;
        let resScanData: ScanData | null = null;

        dispatch(setLoading(true));
        dispatch(setError(''));
        
        try {
            const result = await scanRecipientAction(walletType, recipient, transaction.membersOnly, balance);
            resRecipient = result.recipient;
            resScanData = result.scanData;
        } catch (err: any) {
            console.error('Error in scanRecipient:', err);
            // Handle errors
            if (err instanceof ScanRecipientValidationError) {
                dispatch(setError(err.message));
            } else if (err.response?.data?.error) {
                dispatch(setError(err.response.data.error));
            } else {
                console.error('Error in scanRecipient:', err);
                dispatch(setError('Unknown error'));
            }
        } finally {
            dispatch(setLoading(false));
        }

        const pleb : PlebData | null = resRecipient?.pleb ? resRecipient.pleb.toJson() : null;
        dispatch(setTransactionRecipient({
            ...resRecipient,
            pleb: pleb
        }));

        dispatch(setTransactionScanData(resScanData));
        if(resScanData?.fixedAmountMsat)
            dispatch(setTransactionAmount(resScanData.fixedAmountMsat / 1000));
        else if(resScanData?.minWithdrawableMsat)
            dispatch(setTransactionAmount(resScanData.minWithdrawableMsat / 1000));
        else
            dispatch(setTransactionAmount(undefined));
        dispatch(setTransactionSendingFees(0));
        dispatch(setTransactionAmountIncludesFees(false));

        return {
            recipient: resRecipient,
            scanData: resScanData
        };
    };

    const scanAndPayTransaction = async (params: Omit<SendTransactionState, 'scanData'>) => {
        let ret: Transaction | null = null;

        dispatch(setLoading(true));
        dispatch(setError(''));

        try {
            if (!params.recipient) {
                throw new Error('Recipient not found');
            }

            const scanResult = await scanRecipient(params.recipient);

            ret = await payFromTransactionState(walletType, {
                ...params,
                scanData: scanResult.scanData,
                recipient: scanResult.recipient
            }, false, identityPubkey);

            if(ret) {
                dispatch(resetTransaction());
                dispatch(addOrReplaceTransaction(ret));

                if(walletType === WalletType.INTERNAL) {
                    refreshBalance();
                }
            }
        } catch (err: any) {
            dispatch(setError(err.message));
        }

        dispatch(setLoading(false));

        return ret;
    }

    return {
        transactionState: transaction,
        recipient: transactionState.recipient,
        scanData: transaction.scanData,
        amount: transaction.amount,
        sendingFees: transaction.sendingFees,
        amountIncludesFees: transaction.amountIncludesFees,
        comment: transaction.comment,
        imageUrl: transaction.imageUrl,
        anonymous: transaction.anonymous,
        membersOnly: transaction.membersOnly,
        minAmountSpendable,
        maxAmountSpendable,
        isRecipientValid,
        isAmountValid,
        error: transaction.error,
        loading: transaction.loading,
        preparePayment,
        payTransaction,
        scanAndPayTransaction,
        scanRecipient,
        setTransactionAmount: (amount: number) => {
            dispatch(setTransactionAmount(amount));
        },
        setTransactionComment: (comment: string) => {
            dispatch(setTransactionComment(comment));
        },
        setTransactionImageUrl: (imageUrl?: string) => {
            dispatch(setTransactionImageUrl(imageUrl));
        },
        setTransactionAnonymous: (anonymous: boolean) => {
            dispatch(setTransactionAnonymous(anonymous));
        },
        setTransactionMembersOnly: (membersOnly: boolean) => {
            dispatch(setTransactionMembersOnly(membersOnly));
        },
        resetTransaction: () => {
            dispatch(resetTransaction());
        },
    }
}
